/**
 * Webナビゲーション互換層
 * React Routerの遷移を既存画面が利用するnavigation/route契約へ変換します。
 */

import React, { useCallback, useEffect, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { VISITOR_ROUTES } from "./routeNames";

/** ルート名と公開URLの対応 */
export const VISITOR_ROUTE_PATHS = Object.freeze({
  [VISITOR_ROUTES.ENTRY]: "/",
  [VISITOR_ROUTES.LOGIN]: "/login",
  [VISITOR_ROUTES.REGISTER]: "/register",
  [VISITOR_ROUTES.HOME]: "/home",
  [VISITOR_ROUTES.NOTICES]: "/notices",
  [VISITOR_ROUTES.EVENTS]: "/events",
  [VISITOR_ROUTES.SCAN]: "/scan",
  [VISITOR_ROUTES.REWARDS]: "/rewards",
  [VISITOR_ROUTES.REDEEM_QR]: "/redeem-qr",
  [VISITOR_ROUTES.REDEMPTIONS]: "/redemptions",
  [VISITOR_ROUTES.SURVEYS]: "/surveys",
  [VISITOR_ROUTES.STALL_RATING]: "/stall-rating",
  [VISITOR_ROUTES.RANKING]: "/ranking",
  [VISITOR_ROUTES.GROUP]: "/group",
  [VISITOR_ROUTES.CAMPUS_MAP]: "/campus-map",
});

/**
 * 表示中のURLのパスからルート名を返す
 * @param {string} pathname - location.pathname
 * @returns {string | null} ルート名（来場者ルートでなければnull）
 */
export const resolveVisitorRouteName = (pathname: string): string | null =>
  Object.keys(VISITOR_ROUTE_PATHS).find(
    (routeName) => VISITOR_ROUTE_PATHS[routeName] === pathname,
  ) ?? null;

/**
 * URLクエリを画面パラメータへ変換する
 * @param {string} search - URLクエリ
 * @returns {Record<string, string>} パラメータ
 */
const parseSearchParams = (search: string) =>
  Object.fromEntries(new URLSearchParams(search).entries());

/**
 * 画面コンポーネントへ渡すWebナビゲーションを作成する
 * @param {string} routeName - 表示中ルート名
 * @returns {{navigation: Object, route: Object}} 互換プロパティ
 */
export const useWebScreenProps = (routeName: string) => {
  const navigateTo = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const queryParams = useMemo(
    () => parseSearchParams(location.search),
    [location.search],
  );
  const routeParams = useMemo(
    () => ({
      ...queryParams,
      ...((location.state as Record<string, any> | null) ?? {}),
    }),
    [location.state, queryParams],
  );

  const resolvePath = useCallback((targetRouteName: string) => {
    const path = VISITOR_ROUTE_PATHS[targetRouteName];
    if (!path) {
      throw new Error(`画面URLが定義されていません: ${targetRouteName}`);
    }
    return path;
  }, []);

  const navigation = useMemo(
    () => ({
      /**
       * 指定画面へ進む
       * @param {string} targetRouteName - 遷移先ルート名
       * @param {Record<string, any>} params - 画面パラメータ
       * @returns {void}
       */
      navigate: (
        targetRouteName: string,
        params: Record<string, any> = {},
      ) => navigateTo(resolvePath(targetRouteName), { state: params }),

      /**
       * 現在履歴を置き換えて指定画面へ進む
       * @param {string} targetRouteName - 遷移先ルート名
       * @param {Record<string, any>} params - 画面パラメータ
       * @returns {void}
       */
      replace: (
        targetRouteName: string,
        params: Record<string, any> = {},
      ) =>
        navigateTo(resolvePath(targetRouteName), {
          replace: true,
          state: params,
        }),

      /**
       * 履歴を指定ルートへリセットする
       * @param {{routes?: Array<{name: string, params?: Record<string, any>}>}} resetState - リセット設定
       * @returns {void}
       */
      reset: (resetState: {
        routes?: Array<{ name: string; params?: Record<string, any> }>;
      }) => {
        const destination =
          resetState?.routes?.[resetState.routes.length - 1] ?? null;
        if (!destination) {
          return;
        }
        navigateTo(resolvePath(destination.name), {
          replace: true,
          state: destination.params ?? {},
        });
      },

      /**
       * 一つ前の画面へ戻る
       * @returns {void}
       */
      goBack: () => navigateTo(-1),

      /**
       * 画面フォーカス時処理を登録する
       * Webでは画面へ戻る遷移でも画面がマウントし直されるため、マウント時の取得は各画面が直接行います。
       * focusは、表示中の画面へブラウザのタブ・アプリから戻った時（visibilitychangeで表示状態になった時）だけ通知します。
       * 登録直後には実行しません。
       * @param {string} eventName - イベント名
       * @param {() => void} listener - 処理
       * @returns {() => void} 解除処理
       */
      addListener: (eventName: string, listener: () => void) => {
        if (eventName !== "focus") {
          return () => {};
        }

        /**
         * 画面が表示状態へ戻った時だけ処理を呼ぶ
         * @returns {void}
         */
        const handleVisibilityChange = () => {
          if (document.visibilityState === "visible") {
            listener();
          }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange,
          );
        };
      },

      /**
       * 現在画面がフォーカス中か返す
       * @returns {boolean} 常にtrue
       */
      isFocused: () => true,

      /**
       * 親ナビゲーション互換値を返す
       * @returns {null} Webでは親なし
       */
      getParent: () => null,
    }),
    [navigateTo, resolvePath],
  );

  return {
    navigation,
    route: {
      key: `${routeName}-${navigationType}-${location.key}`,
      name: routeName,
      params: routeParams,
      path: location.pathname,
    },
  };
};

/**
 * React NavigationのuseFocusEffect互換フック
 * 画面マウント時に副作用を実行します。
 * @param {React.EffectCallback} effect - フォーカス時副作用
 * @returns {void}
 */
export const useFocusEffect = (effect: React.EffectCallback) => {
  useEffect(effect, [effect]);
};

/**
 * 現在画面のフォーカス状態を返す
 * @returns {boolean} 表示中画面ではtrue
 */
export const useIsFocused = () => true;

